import { canonicalActionTagFor } from 'state/aiModels'
import { DEFAULT_VIDEO_MODEL } from 'state/defaultAiModels'

const BRACKET_REFERENCE_PROMPT_COMMAND_TAGS = new Set([
    'seedance',
    'seedance1',
    'seedance2',
    DEFAULT_VIDEO_MODEL.actionTag,
])
const ANGLE_REFERENCE_PROMPT_COMMAND_TAGS = new Set(['grokvideo1.5'])

const normalizePromptReferenceCommandTag = value =>
    String(value || '')
        .trim()
        .replace(/^!+/, '')
        .toLowerCase()

const effectivePromptReferenceCommandTag = actionLike => {
    const settings = actionLike?.settings || {}
    const tag = normalizePromptReferenceCommandTag(
        actionLike?.tag || actionLike?.actionTag || actionLike?.action_tag,
    )

    if (tag === 'extend') {
        return (
            canonicalActionTagFor(
                actionLike?.extendActionTag ||
                    actionLike?.extend_action_tag ||
                    settings.extend_action_tag ||
                    settings.extendActionTag,
            ) || tag
        )
    }

    return canonicalActionTagFor(tag)
}

const promptReferenceSyntaxForAction = actionLike =>
    BRACKET_REFERENCE_PROMPT_COMMAND_TAGS.has(effectivePromptReferenceCommandTag(actionLike))
        ? 'bracket'
        : ANGLE_REFERENCE_PROMPT_COMMAND_TAGS.has(effectivePromptReferenceCommandTag(actionLike))
        ? 'angle'
        : 'mention'

const escapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const replaceMentionToken = (prompt, label, replacement) => {
    const escapedLabel = escapeRegExp(label)
    return prompt.replace(new RegExp(`(^|[^\\w])@${escapedLabel}\\b`, 'gi'), (_, prefix) => {
        return `${prefix}${replacement}`
    })
}

const replaceBracketToken = (prompt, label, replacement) => {
    return prompt.replace(new RegExp(`\\[${escapeRegExp(label)}\\]`, 'gi'), replacement)
}

const labelsForCount = (prefix, count) =>
    Array.from({ length: Math.max(0, Number(count) || 0) }, (_, index) => `${prefix}${index + 1}`)

export const promptReferenceLabelsForAttrs = attrs => [
    ...labelsForCount(
        'image',
        attrs?.referenceImageInputs?.length || attrs?.inputs?.references?.images?.length,
    ),
    ...labelsForCount(
        'video',
        attrs?.referenceVideoInputs?.length || attrs?.inputs?.references?.videos?.length,
    ),
    ...labelsForCount(
        'audio',
        attrs?.referenceAudioInputs?.length || attrs?.inputs?.references?.audios?.length,
    ),
]

export const serializePromptReferenceMentions = (prompt, actionLike, labels = null) => {
    const sourcePrompt = String(prompt || '')
    const referenceLabels = labels || promptReferenceLabelsForAttrs(actionLike)
    if (!referenceLabels.length) return sourcePrompt

    const syntax = promptReferenceSyntaxForAction(actionLike)
    if (syntax === 'mention') return sourcePrompt

    return referenceLabels.reduce((nextPrompt, label) => {
        const normalizedLabel = label.toLowerCase()
        const xaiLabel = `<${normalizedLabel.replace(/^([a-z]+)(\d+)$/, '$1_$2').toUpperCase()}>`
        const replacement = syntax === 'angle' ? xaiLabel : `[${normalizedLabel}]`
        return replaceBracketToken(
            replaceMentionToken(nextPrompt, label, replacement),
            label,
            replacement,
        )
    }, sourcePrompt)
}

export const deserializePromptReferenceMentions = (prompt, actionLike, labels = null) => {
    const sourcePrompt = String(prompt || '')
    const referenceLabels = labels || promptReferenceLabelsForAttrs(actionLike)
    if (!referenceLabels.length) return sourcePrompt

    const syntax = promptReferenceSyntaxForAction(actionLike)
    if (syntax === 'mention') return sourcePrompt

    return referenceLabels.reduce((nextPrompt, label) => {
        const normalizedLabel = label.toLowerCase()
        const xaiLabel = `<${normalizedLabel.replace(/^([a-z]+)(\d+)$/, '$1_$2').toUpperCase()}>`
        const promptWithBracket = replaceBracketToken(nextPrompt, label, `@${normalizedLabel}`)
        return syntax === 'angle'
            ? promptWithBracket.replace(
                  new RegExp(escapeRegExp(xaiLabel), 'gi'),
                  `@${normalizedLabel}`,
              )
            : promptWithBracket
    }, sourcePrompt)
}
